import { useState, useEffect } from "react";
import { writeContract,readContract, waitForTransactionReceipt } from "@wagmi/core";
import { abi } from "./abi";
import { config } from "./config";
import { transformForOnchain } from "@reclaimprotocol/js-sdk";
import { useSimulateContract, useWaitForTransactionReceipt, useWriteContract } from "wagmi";
import { toast } from './App';

export default function VerifyProof({ proof, onVerified }) {
  const [transformedProof, setTransformedProof] = useState({});
  const [verified, setVerified] = useState(false);
  const [showLoader, setShowLoader] = useState(false);

  useEffect(() => {
    const newProof = transformForOnchain(proof);
    setTransformedProof(newProof);
  }, [proof]);

  // Simulate the contract call to get the return value
  const { 
    data: simulationResult, 
    error: simulationError,
    isLoading: isSimulating 
  } = useSimulateContract({
    abi,
    address: "0x583F5bCc04FE351eB13D7F8D9Ca98C8A72FF229B",
    functionName: "verifyProof",
    chainId: 11155111,
    args: [transformedProof],
    query: {
      enabled: !!transformedProof && Object.keys(transformedProof).length > 0,
    }
  });

  const { writeContract, data: hash, error, isPending } = useWriteContract();
  
  const { 
    isLoading: isConfirming, 
    isSuccess: isConfirmed,
    data: receipt
  } = useWaitForTransactionReceipt({
    hash,
  });

  useEffect(() => {
    if (isConfirming) {
      setShowLoader(true);
    } else {
      setShowLoader(false);
    }
  }, [isConfirming]);

  useEffect(() => {
    if (isConfirmed && receipt) {
      setShowLoader(true); // Keep loader while onVerified runs
      setVerified(true);
      const rawResult = simulationResult?.result;
      if (rawResult) {
        const numericString = [...rawResult].filter(char => !isNaN(char) && char !== ' ').join('');
        const numericResult = Number(numericString);
        if (numericResult > 1000) {
          toast.success('Proof verified! You are eligible.');
          // Call onVerified and hide loader after
          Promise.resolve(onVerified(hash, numericResult)).finally(() => setShowLoader(false));
        } else {
          setShowLoader(false);
        }
      } else {
        setShowLoader(false);
      }
    }
  }, [isConfirmed, receipt, simulationResult, hash, onVerified]);

  return (
    <div className="relative">
      {/* Loader overlay */}
      {showLoader && (
        <div className="absolute inset-0 flex flex-col items-center justify-center bg-white/80 z-20 rounded-xl animate-fade-in">
          <div className="mb-4 animate-spin-slow">
            <span role="img" aria-label="croissant" className="text-6xl">🥐</span>
          </div>
          <p className="text-xl font-bold text-yellow-900">Confirming your transaction...</p>
        </div>
      )}
      <div className="bg-green-100 border-4 border-black p-6 shadow-[8px_8px_0px_0px_rgba(0,0,0,1)] rounded-xl">
        <h2 className="text-black font-bold text-3xl mb-6">VERIFY PROOF</h2>
        
        <button
          className="bg-orange-200 border-4 border-black px-8 py-4 font-bold text-black text-xl hover:bg-orange-300 transition-colors shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] hover:shadow-[6px_6px_0px_0px_rgba(0,0,0,1)] disabled:opacity-50 disabled:cursor-not-allowed"
          onClick={() => {
            console.log("Proof to verify:", transformedProof);
            console.log("Simulation result:", simulationResult?.result);
            
            if (simulationResult?.request) {
              writeContract(simulationResult.request);
            }
          }}
          disabled={isPending || isConfirming || isSimulating}
        >
          {isPending ? "CONFIRMING..." : isConfirming ? "WAITING..." : "VERIFY PROOF"}
        </button>

        {simulationError && (
          <div className="mt-6 bg-red-100 border-4 border-black p-4 shadow-[4px_4px_0px_0px_rgba(0,0,0,1)]">
            <h3 className="text-black font-bold text-lg mb-2">SIMULATION ERROR:</h3>
            <p className="text-black">{simulationError.message}</p>
            {toast.error('Simulation error: ' + simulationError.message)}
          </div>
        )}

        {hash && (
          <div className="mt-6 bg-yellow-100 border-4 border-black p-4 shadow-[4px_4px_0px_0px_rgba(0,0,0,1)]">
            <h3 className="text-black font-bold text-lg mb-2">TRANSACTION HASH:</h3>
            <p className="text-black font-mono text-sm break-all" onClick={() => {navigator.clipboard.writeText(hash); toast.success('Transaction hash copied!')}} style={{cursor:'pointer'}}>{hash}</p>
          </div>
        )}

        {isConfirming && (
          <div className="mt-6 bg-purple-100 border-4 border-black p-4 shadow-[4px_4px_0px_0px_rgba(0,0,0,1)]">
            <p className="text-black font-bold">WAITING FOR CONFIRMATION...</p>
          </div>
        )}

        {verified && (
          <div className="mt-6 bg-green-200 border-4 border-black p-4 shadow-[4px_4px_0px_0px_rgba(0,0,0,1)]">
            <p className="text-black font-bold text-lg">PROOF VERIFIED SUCCESSFULLY!</p>
          </div>
        )}

        {error && (
          <div className="mt-6 bg-red-100 border-4 border-black p-4 shadow-[4px_4px_0px_0px_rgba(0,0,0,1)]">
            <h3 className="text-black font-bold text-lg mb-2">TRANSACTION ERROR:</h3>
            <p className="text-black">{error.message}</p>
            {toast.error('Transaction error: ' + error.message)}
          </div>
        )}
      </div>
    </div>
  );
}
